import { useEffect, useMemo, useState } from "react";
import { QrCode, Search } from "lucide-react";
import { Contact as User } from "@/data/schemas";
import UsersList from "@/ui/components/UsersList";
import UserProfilePane from "@/features/profile/screens/UserProfilePane";
import SearchUserPane from "@/features/contacts/screens/SearchUserPane";
import { ShowMyQrPane } from "@/features/contacts/screens/ShowMyQrScreen";
import { Button } from "@/ui/components/ui/button";
import { useStore } from "@/state/legacy_module_api";

type RightPane = "profile" | "search" | "qr";

const ContactsNav = () => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [rightPane, setRightPane] = useState<RightPane>("profile");
  const usersTabShowSearch = useStore((s) => s.usersTabShowSearch);
  const setUsersTabShowSearch = useStore((s) => s.setUsersTabShowSearch);

  // Sync with store state (for popover trigger)
  useEffect(() => {
    if (usersTabShowSearch) {
      setRightPane("search");
      setUsersTabShowSearch(false);
    }
  }, [usersTabShowSearch, setUsersTabShowSearch]);

  const handleProfileClick = (user: User) => {
    console.log("[ContactsNav] handleProfileClick", { userId: user.userId });
    setSelectedUser(user);
    setRightPane("profile");
  };

  const handleSearchClick = () => {
    setRightPane("search");
  };

  const handleShowMyQrClick = () => {
    setRightPane("qr");
  };

  const handleUserAdded = (user: User) => {
    setSelectedUser(user);
    setRightPane("profile");
  };

  const selectedUserId = useMemo(
    () => selectedUser?.userId,
    [selectedUser]
  );

  return (
    <div data-name="contacts-nav" className="h-full grid grid-cols-[minmax(320px,400px)_1fr] min-h-0">
      <div data-name="contacts-sidebar" className="h-full overflow-auto border-r flex flex-col">
        <div data-name="contacts-header" className="flex items-center justify-between p-4 border-b">
          <h2 className="font-semibold">Contacts</h2>
          <div className="flex items-center gap-1">
            <Button
              data-name="search-user-button"
              variant="ghost"
              size="sm"
              onClick={handleSearchClick}
              title="Find User"
            >
              <Search className="w-4 h-4" />
            </Button>
            <Button
              data-name="show-my-qr-button"
              variant="ghost"
              size="sm"
              onClick={handleShowMyQrClick}
              title="Show My QR"
            >
              <QrCode className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div data-name="contacts-list" className="flex-1 overflow-auto p-4">
          <UsersList
            onProfilePictureClick={handleProfileClick}
            onUserClick={handleProfileClick}
          />
        </div>
      </div>
      <div data-name="contacts-panel" className="h-full">
        {rightPane === "search" ? (
          <SearchUserPane
            onBack={() => setRightPane("profile")}
            onUserAdded={handleUserAdded}
          />
        ) : rightPane === "qr" ? (
          <ShowMyQrPane />
        ) : (
          <UserProfilePane userId={selectedUserId} />
        )}
      </div>
    </div>
  );
};

export default ContactsNav;
